// src/pages/events/page.tsx
import { useMemo, useState } from "react";
import { useEvents } from "@/lib/api/hooks";
import { PageHeader } from "@/components/layout/page-header";
import { Plus, Calendar, Search, Users, CalendarClock } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EventForm } from "./components/event-form";
import { EventListItem } from "./components/event-list-item";
import { RSVPSheet } from "./components/rsvp-sheet";
import { EmptyState } from "@/components/ui/empty-state";
import { Event } from "@/lib/api/types";
import { Card, CardContent } from "@/components/ui/card";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import { usePermission } from "@/lib/contexts/permission-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { isEventPast, parseEventDate } from "@/lib/utils/events";

type Filter = "upcoming" | "past" | "all";

function EventsSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading events">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="overflow-hidden rounded-lg border-border/50">
          <CardContent className="flex flex-col p-0 sm:flex-row">
            <Skeleton className="h-40 w-full rounded-none sm:h-44 sm:w-48" />
            <div className="flex-1 space-y-3 p-5">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-1.5 w-full" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function EventsPage() {
  const { events = [], isLoading, create, update, delete: deleteEvent } = useEvents();
  const { hasPermission } = usePermission();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [rsvpEventId, setRsvpEventId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("upcoming");
  const [search, setSearch] = useState("");

  // Look the event up from the query cache so counts stay current after RSVP changes
  const rsvpEvent = events.find((e) => e.id === rsvpEventId) ?? null;

  const { upcoming, past } = useMemo(() => {
    const byDate = (a: Event, b: Event) => parseEventDate(a.date).getTime() - parseEventDate(b.date).getTime();
    const upcoming = events.filter((e) => !isEventPast(e.date)).sort(byDate);
    const past = events.filter((e) => isEventPast(e.date)).sort((a, b) => byDate(b, a));
    return { upcoming, past };
  }, [events]);

  const visible = useMemo(() => {
    const list = filter === "upcoming" ? upcoming : filter === "past" ? past : [...upcoming, ...past];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => e.title.toLowerCase().includes(q) || e.location.toLowerCase().includes(q));
  }, [filter, upcoming, past, search]);

  const upcomingRsvps = upcoming.reduce((sum, e) => sum + (e.rsvpCount ?? 0), 0);

  const handleCreateUpdate = async (data: FormData) => {
    try {
      if (selectedEvent) {
        await update({ id: selectedEvent.id, data });
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      setSelectedEvent(null);
    } catch {
      // Keep the form open so the admin can fix the problem; the API client shows the error
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      await deleteEvent(selectedEvent.id);
      setIsDeleteDialogOpen(false);
      setSelectedEvent(null);
    } catch {
      // Error toast is shown by the API client
    }
  };

  const openCreate = () => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const tabs: { value: Filter; label: string; count: number }[] = [
    { value: "upcoming", label: "Upcoming", count: upcoming.length },
    { value: "past", label: "Past", count: past.length },
    { value: "all", label: "All", count: events.length },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        description="Create events and manage who has RSVP'd."
        action={
          <PermissionGuard resource="events" action="create">
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Event
            </Button>
          </PermissionGuard>
        }
      />

      {isLoading ? (
        <EventsSkeleton />
      ) : events.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No events yet"
          description="Create your first event and it will appear on the website for people to RSVP."
          action={hasPermission("events", "create") ? { label: "Add Event", onClick: openCreate } : undefined}
        />
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card className="rounded-lg border-border/50">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-md bg-primary/10 p-2">
                  <CalendarClock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Upcoming events</p>
                  <p className="text-xl font-semibold tabular-nums text-foreground">{upcoming.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-lg border-border/50">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="rounded-md bg-primary/10 p-2">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">RSVPs for upcoming events</p>
                  <p className="text-xl font-semibold tabular-nums text-foreground">{upcomingRsvps}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" role="tablist" aria-label="Filter events">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={filter === tab.value}
                  onClick={() => setFilter(tab.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    filter === tab.value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 tabular-nums text-xs text-muted-foreground">{tab.count}</span>
                </button>
              ))}
            </div>
            <div className="relative sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events"
                className="pl-9"
                aria-label="Search events"
              />
            </div>
          </div>

          {visible.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
              {search
                ? `No events match "${search}".`
                : filter === "upcoming"
                  ? "No upcoming events. Past events are under the Past tab."
                  : "No past events yet."}
            </p>
          ) : (
            <div className="space-y-4">
              {visible.map((event) => (
                <EventListItem
                  key={event.id}
                  event={event}
                  onViewRSVPs={(e) => setRsvpEventId(e.id)}
                  onEdit={(e) => {
                    setSelectedEvent(e);
                    setIsModalOpen(true);
                  }}
                  onDelete={(e) => {
                    setSelectedEvent(e);
                    setIsDeleteDialogOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <RSVPSheet event={rsvpEvent} onClose={() => setRsvpEventId(null)} />

      <Modal
        title={selectedEvent ? "Edit Event" : "Add Event"}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEvent(null);
        }}
      >
        {/* Remount per event so the form's defaults match the event being edited */}
        <EventForm key={selectedEvent?.id ?? "new"} initialData={selectedEvent} onSubmit={handleCreateUpdate} />
      </Modal>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleDelete}
        title="Delete Event"
        description={
          selectedEvent?.rsvpCount
            ? `Delete "${selectedEvent.title}" and its ${selectedEvent.rsvpCount} RSVP${selectedEvent.rsvpCount === 1 ? "" : "s"}? This can't be undone.`
            : `Delete "${selectedEvent?.title ?? "this event"}"? This can't be undone.`
        }
      />
    </div>
  );
}
